// client/src/components/BottomNavigation.tsx
import React, { useState } from 'react';
import { Camera, Cloud, Bug, Mic, Home } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext.tsx';

interface BottomNavigationProps {
  activeTab: number;
  onTabChange: (tabIndex: number) => void;
  onCropAnalysis: () => void;
  onVoiceQuery: () => void;
  user: any;
}

export default function BottomNavigation({ 
  activeTab, 
  onTabChange, 
  onCropAnalysis, 
  onVoiceQuery,
  user 
}: BottomNavigationProps) {
  // Safety check to prevent hooks from breaking
  if (typeof React === 'undefined' || !React.useState) {
    return null;
  }
  
  const { t } = useLanguage();
  const [showAuthMessage, setShowAuthMessage] = useState(false);
  const [authMessage, setAuthMessage] = useState('');

  const showAuthToast = (feature: string) => {
    setAuthMessage(`${t?.('features.signInRequired') || 'Sign in required'} - ${feature}`);
    setShowAuthMessage(true);
    setTimeout(() => setShowAuthMessage(false), 3000);
  };

  const handleAction = (action: () => void, requiresAuth: boolean, feature: string) => {
    try {
      if (requiresAuth && !user) {
        showAuthToast(feature);
        return;
      }
      action();
    } catch (error) {
      console.error(`Navigation error for ${feature}:`, error);
    }
  };

  const navItems = [
    {
      icon: <Home className="w-6 h-6" />,
      label: 'Home',
      action: () => handleAction(() => onTabChange(0), false, 'Home'),
      requiresAuth: false,
      tabIndex: 0
    },
    {
      icon: <Camera className="w-6 h-6" />,
      label: 'Crops',
      action: () => handleAction(onCropAnalysis, true, 'Crops'),
      requiresAuth: true,
      tabIndex: 1
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      label: 'Weather',
      action: () => handleAction(() => onTabChange(2), false, 'Weather'),
      requiresAuth: false,
      tabIndex: 2
    },
    {
      icon: <Mic className="w-6 h-6" />,
      label: 'Voice',
      action: () => handleAction(onVoiceQuery, true, 'Voice'),
      requiresAuth: true,
      tabIndex: 3
    },
    {
      icon: <Bug className="w-6 h-6" />,
      label: 'Advisor',
      action: () => handleAction(() => onTabChange(3), true, 'Advisor'),
      requiresAuth: true,
      tabIndex: 3
    }
  ];

  return (
    <>
      {/* Auth Toast Message */}
      {showAuthMessage && (
        <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 md:hidden">
          <div className="flex items-center gap-2">
            <span className="text-sm">{authMessage}</span>
          </div>
        </div>
      )}
      
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg md:hidden z-50 safe-area-inset-bottom">
        <div className="flex justify-around items-center py-2 px-1">
          {navItems.map((item, index) => {
            const isActive = activeTab === item.tabIndex;
            const needsAuth = item.requiresAuth && !user;
            
            return (
              <button
                key={index}
                onClick={item.action}
                className={`relative flex flex-col items-center justify-center p-2 min-w-0 flex-1 min-h-[60px] ${
                  isActive 
                    ? 'text-green-600 bg-green-50' 
                    : needsAuth 
                      ? 'text-gray-400' 
                      : 'text-gray-600 hover:text-green-600 hover:bg-gray-50'
                } transition-all duration-200 rounded-lg mx-0.5`}
                aria-label={item.label}
                disabled={needsAuth}
              >
                <div className="flex flex-col items-center">
                  {item.icon}
                  <span className="text-xs mt-1 truncate max-w-full leading-tight">{item.label}</span>
                </div>
                {needsAuth && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                )}
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-green-600 rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}